/**
 * Ask — typed fetch helpers over api/src/wow/routes.ts (`/api/ask/status`,
 * `/api/ask`). `/ask/status` is plain JSON and always 200, so it goes through the shared
 * `apiFetch`. `/ask` answers as an SSE stream, which `apiFetch` cannot express (it always reads
 * the body as one JSON document), so it gets its own `fetch` here — same auth headers, same base
 * path, same error-shape handling, just reading the body frame-by-frame instead of once.
 *
 * No EventSource: it can only GET, and asking a question is a POST body.
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch, getAuthHeaders } from '@/api/client';
import { envOr } from '@/lib/env';
import type { AskEvent, AskStatus } from './types';

/** Same fallback as src/api/client.ts — this module makes its own fetch call and cannot import BASE from there (it is not exported). */
const BASE = envOr(import.meta.env.VITE_API_BASE, '/api');

export const qk = {
  status: ['ask', 'status'] as const,
};

export function useAskStatus() {
  return useQuery<AskStatus>({
    queryKey: qk.status,
    staleTime: 5 * 60_000,
    queryFn: () => apiFetch<AskStatus>('/ask/status'),
  });
}

export const NOT_A_STREAM = 'Ask accepted the question as a background job, not a stream, so no answer will appear here. Ask again; if it keeps happening, report it from the ⋯ menu.';

export interface StreamAskInput {
  question: string;
  signal?: AbortSignal;
  onEvent: (event: AskEvent) => void;
}

/**
 * Parses one SSE frame ("event: x\ndata: {...}") into a typed AskEvent. The server always pairs
 * `event:` with a `data:` line whose JSON already carries a matching `type` field, so the frame's
 * own `event:` line is only used to drop anything malformed rather than re-derive the type.
 */
function parseFrame(frame: string): AskEvent | null {
  let sawData = false;
  const dataLines: string[] = [];
  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (line.startsWith('data:')) {
      sawData = true;
      dataLines.push(line.slice(5).replace(/^ /, ''));
    }
  }
  if (!sawData) return null;
  try {
    return JSON.parse(dataLines.join('\n')) as AskEvent;
  } catch {
    return null;
  }
}

/**
 * POSTs the question and streams the answer back. Resolves once the stream ends (after `done` or
 * `error`, or when the caller aborts via `signal`). Throws only for a transport-level failure —
 * a non-2xx response (503 dependency_down, 403, …), whose JSON body's `error.message` is surfaced
 * verbatim, since that is the one shape this route's errors actually use.
 */
export async function streamAsk({ question, signal, onEvent }: StreamAskInput): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    ...(await getAuthHeaders()),
  };

  const res = await fetch(`${BASE}/ask`, {
    method: 'POST',
    headers,
    // R-16 T-126: no operationId — since 2721880d0 one sent here turned the stream into a job (202 JSON) and the page heard nothing
    body: JSON.stringify({ question }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text();
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = text ? JSON.parse(text) : null;
      const nested = parsed?.error?.message;
      if (typeof nested === 'string' && nested) message = nested;
    } catch {
      /* keep the status-line message */
    }
    throw new Error(message);
  }

  // a job acceptance or any other JSON body is not the answer: say so (the caller shows it), never wait on silence
  if (!(res.headers.get('content-type') ?? '').includes('text/event-stream')) throw new Error(NOT_A_STREAM);
  if (!res.body) return;

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let separatorIndex: number;
    while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);
      const event = parseFrame(frame);
      if (event) onEvent(event);
    }
  }

  const trailing = buffer.trim();
  if (trailing) {
    const event = parseFrame(trailing);
    if (event) onEvent(event);
  }
}
